import { useEffect, useState } from "react";
import { Target } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtINR, fmtRate } from "@/lib/format";

const monthLabel = (m) => new Date(`${m}-01T00:00:00Z`).toLocaleString("en-IN", { month: "long", year: "numeric", timeZone: "UTC" });

export const GoalCard = ({ goal, onSaved }) => {
  const [value, setValue] = useState(goal.amount ?? "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setValue(goal.amount ?? ""); }, [goal.amount, goal.month]);

  const save = async (e) => {
    e.preventDefault();
    const n = Number(value);
    if (value === "" || Number.isNaN(n) || n <= 0) return setError("Enter a goal above ₹0");
    setBusy(true);
    try {
      await api.put(`/goals/${goal.month}`, { amount: n });
      setError("");
      toast.success("Revenue goal saved");
      onSaved();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const pct = goal.completion_pct;
  return (
    <form onSubmit={save} data-testid="goal-card" className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400"><Target className="h-3.5 w-3.5" /> Revenue goal · {monthLabel(goal.month)}</p>
      <div className="mt-2 flex gap-2">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">₹</span>
          <input data-testid="goal-input" type="number" inputMode="decimal" value={value} onChange={(e) => { setValue(e.target.value); setError(""); }}
            placeholder="Set a goal" className={`inp pl-7 font-mono ${error ? "inp-invalid" : ""}`} />
        </div>
        <button type="submit" disabled={busy} data-testid="goal-save-btn" className="btn-primary">Save</button>
      </div>
      {error && <p data-testid="goal-input-error" className="field-error mt-1">{error}</p>}
      <div className="mt-3 flex items-end justify-between">
        <div>
          <p className="text-xs text-slate-400">Cash this month</p>
          <p data-testid="goal-month-cash" className="font-mono font-semibold">{fmtINR(goal.cash_collected)}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-400">Goal completion</p>
          <p data-testid="goal-completion-pct" className="font-mono text-2xl font-semibold text-indigo-300">{fmtRate(pct)}</p>
        </div>
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-800">
        <div className="h-full rounded-full bg-indigo-500" style={{ width: `${Math.min(pct || 0, 100)}%`, transition: "width 400ms ease" }} />
      </div>
    </form>
  );
};
